import React from "react";
import { LucideIcon } from "lucide-react";

type SummaryTone = "neutral" | "positive" | "negative" | "info" | "warning";

/** Badge do ícone por tom semântico — superfície do card mantém-se sempre neutra. */
const TONE_BADGE: Record<SummaryTone, string> = {
    neutral: "bg-slate-100 text-slate-500 dark:bg-zinc-700/50 dark:text-zinc-300",
    positive: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
    negative: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400",
    info: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-400",
    warning: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400",
};

export interface SummaryCardItem {
    title: string;
    value: string;
    subValue?: React.ReactNode;
    icon?: LucideIcon;
    /** Tom semântico que colore o badge do ícone (default: neutral). */
    tone?: SummaryTone;
    titleClass?: string;
    valueClass?: string;
    subValueClass?: string;
    loading?: boolean;
    onClick?: () => void;
    /** @deprecated mantido por compatibilidade — a superfície é sempre neutra. */
    toneClass?: string;
    /** @deprecated mantido por compatibilidade. */
    staticTheme?: boolean;
}

interface SummaryCardsProps {
    cards: SummaryCardItem[];
    className?: string;
    valueClass?: string;
    subValueClass?: string;
}

function SummaryCard({
    title,
    value,
    subValue,
    icon: Icon,
    tone = "neutral",
    loading,
    valueClass,
    subValueClass,
    titleClass,
    onClick,
}: SummaryCardItem) {
    return (
        <article
            onClick={onClick}
            className={`rounded-lg border border-slate-200 dark:border-zinc-700 bg-white dark:bg-night_surface px-3 py-3 sm:px-4 sm:py-3.5 min-w-0 flex flex-col justify-between shadow-sm relative ${
                onClick ? "cursor-pointer hover:shadow-md transition-all hover:-translate-y-0.5" : ""
            }`}
        >
            <div className="flex justify-between items-start gap-2">
                <span
                    className={`text-[11px] uppercase tracking-wider font-semibold text-dark_gray dark:text-zinc-400 ${titleClass || ""}`}
                >
                    {title}
                </span>
                {Icon ? (
                    <span className={`max-sm:hidden flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0 ${TONE_BADGE[tone]}`}>
                        <Icon size={18} />
                    </span>
                ) : null}
            </div>

            <div className="mt-3 flex-1 flex flex-col justify-end">
                {loading ? (
                    <div className="h-7 w-24 bg-black/10 dark:bg-white/10 rounded animate-pulse" />
                ) : (
                    <p className={`text-xl sm:text-2xl font-semibold text-black dark:text-zinc-100 leading-tight break-words ${valueClass || ""}`}>
                        {value}
                    </p>
                )}
            </div>

            {subValue && (
                <div className="mt-2">
                    {loading ? (
                        <div className="h-3 w-16 bg-black/10 dark:bg-white/10 rounded animate-pulse" />
                    ) : (
                        <span
                            className={`text-[11px] font-medium uppercase tracking-wide flex items-center gap-1 text-dark_gray dark:text-zinc-400 ${subValueClass || ""}`}
                        >
                            {subValue}
                        </span>
                    )}
                </div>
            )}
        </article>
    );
}

export function SummaryCards({ cards, className = "", valueClass, subValueClass }: SummaryCardsProps) {
    return (
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 ${className}`.trim()}>
            {cards.map((card, index) => (
                <SummaryCard
                    key={`${card.title}-${index}`}
                    {...card}
                    valueClass={card.valueClass || valueClass}
                    subValueClass={card.subValueClass || subValueClass}
                />
            ))}
        </div>
    );
}
